/**
 * A section of a screen: an outlined card with a title row — an optional icon,
 * the title, and the section's own actions at the far end — over its content.
 *
 * Every detail and overview screen was writing this out by hand, with the
 * icon, the spacing under the title and the place of a button drifting from
 * copy to copy. One component, so a section looks the same wherever it is.
 */
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface PanelProps {
  /** Omit for a panel that is only a frame — a block of details under the header. */
  title?: ReactNode;
  /** A small icon before the title. Coloured as secondary text here. */
  icon?: ReactNode;
  /** Buttons or a link at the right of the title row: "Assign", "Add", "Whole log". */
  actions?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export function Panel({ title, icon, actions, children, sx }: PanelProps) {
  return (
    <Paper variant="outlined" sx={[{ p: 2.5 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {(title || actions) && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          {icon && <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>}
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {title}
          </Typography>
          {actions}
        </Stack>
      )}
      {children}
    </Paper>
  );
}
